import { useEffect, useState } from 'react';
import type { LevelInfoResponse } from '@ember/shared';
import { useGamificationFx } from '@/contexts/GamificationFxContext';
import { cn } from '@/lib/cn';

interface XpBarProps {
  level: LevelInfoResponse;
  className?: string;
}

export function XpBar({ level, className }: XpBarProps) {
  const fx = useGamificationFx();
  const [displayed, setDisplayed] = useState<LevelInfoResponse>(level);

  useEffect(() => {
    setDisplayed(level);
  }, [level]);

  const pct = Math.round(displayed.progress * 100);
  const remaining = Math.max(0, displayed.xpForNextLevel - displayed.totalXp);

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <span
        className={cn(
          'grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent font-mono text-sm font-bold text-parchment-light shadow-parchment-card transition-transform',
          fx.xpBurst > 0 && 'scale-110',
        )}
        aria-label={`Level ${displayed.level}`}
      >
        {displayed.level}
      </span>
      <div className="flex-1">
        <div className="flex items-baseline justify-between gap-2 text-xs">
          <span className="text-parchment-muted">
            {displayed.totalXp.toLocaleString()} XP
          </span>
          {fx.xpBurst > 0 && (
            <span className="font-semibold text-accent animate-fade-in">+{fx.xpBurst} XP</span>
          )}
          <span className="text-parchment-muted">
            {remaining.toLocaleString()} to L{displayed.level + 1}
          </span>
        </div>
        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-parchment-dark">
          <div
            className="h-full bg-accent transition-[width] duration-700 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  );
}
